import type { ReactNode } from 'react';
import type { ApprovalRequest } from '@/lib/types';
import { BucketBadge } from '@/components/ui/StatusBadge';
import { formatDate } from '../format';
import { DefaultFieldsTable } from './_DefaultFieldsTable';
import { readDefaultFields, readBlurb, readExpenseRebateLines } from './_payload';

export interface BucketShellProps {
  req: ApprovalRequest;
  children?: ReactNode;
  // Hide the canonical default-fields table for buckets that need a custom
  // layout (e.g. terminal `Posted` views show the Oracle readback above it).
  hideDefaultFields?: boolean;
}

export function BucketShell({ req, children, hideDefaultFields }: BucketShellProps) {
  const fields = readDefaultFields(req);
  const blurb = readBlurb(req);
  const expenseRebateLines = readExpenseRebateLines(req);

  return (
    <div className="flex flex-col gap-12">
      <header className="flex flex-wrap items-start justify-between gap-12 rounded-lg border border-neutral-200 bg-neutral-0 p-16">
        <div className="flex flex-col gap-4">
          <h2 className="text-2xl font-semibold text-neutral-900">
            {fields.vendor ?? 'Unknown vendor'}
          </h2>
          <div className="flex flex-wrap items-center gap-x-12 gap-y-4 text-sm text-neutral-600">
            <span>
              Invoice{' '}
              <span className="font-mono text-neutral-800">{fields.invoice_no ?? '—'}</span>
            </span>
            <span>
              NTN <span className="font-mono text-neutral-800">{fields.ntn ?? '—'}</span>
            </span>
            <span>{formatDate(fields.invoice_date ?? null)}</span>
          </div>
        </div>
        <BucketBadge bucket={req.bucket} />
      </header>

      {blurb && <p className="text-sm text-neutral-700">{blurb}</p>}

      {children}

      {!hideDefaultFields && <DefaultFieldsTable fields={fields} />}
      {expenseRebateLines.length > 0 && (
        <section className="rounded-lg border border-neutral-200 bg-neutral-0 p-16">
          <p className="mb-12 text-xs font-semibold uppercase tracking-wide text-neutral-500">
            Exp/Reb lines
          </p>
          <div className="overflow-x-auto rounded-md border border-neutral-200">
            <table className="w-full text-sm">
              <thead className="bg-neutral-50 text-left text-xs font-medium uppercase tracking-wide text-neutral-600">
                <tr>
                  <th className="px-8 py-6">Type</th>
                  <th className="px-8 py-6 text-right">Amount</th>
                  <th className="px-8 py-6">Narration</th>
                  <th className="px-8 py-6">Code ID</th>
                  <th className="px-8 py-6">Charged to</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-100">
                {expenseRebateLines.map((line, index) => (
                  <tr key={index}>
                    <td className="px-8 py-6 font-mono">
                      {String(line.type ?? line.entry_type ?? '—')}
                    </td>
                    <td className="px-8 py-6 text-right font-medium">
                      {line.amount == null || line.amount === '' ? '—' : String(line.amount)}
                    </td>
                    <td className="px-8 py-6">{line.narration ?? '—'}</td>
                    <td className="px-8 py-6 font-mono">
                      {line.exp_reb_code_id == null &&
                      line.exp_reb_code_comb_id == null
                        ? '—'
                        : String(line.exp_reb_code_id ?? line.exp_reb_code_comb_id)}
                    </td>
                    <td className="px-8 py-6">
                      {[
                        line.charged_to_mode ?? line.charged_to,
                        line.charged_to_code_id ?? line.charged_to_code_comb_id,
                      ]
                        .map((value) => (value == null || value === '' ? null : String(value)))
                        .filter(Boolean)
                        .join(' · ') || '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </div>
  );
}
